import manifestData from '../manifest.json';

export type GameStatus = 'active' | 'inactive' | 'in-development';
export type GameType = 'spa-route' | 'static-bundle';

export interface GameAction {
  label: string;
  to: string;
  external?: boolean;
}

export interface ManifestGame {
  slug: string;
  title: string;
  description?: string;
  emoji?: string;
  status: GameStatus;
  type: GameType;
  entry: string;
  actions?: GameAction[];
  sourcePath?: string;
  pipeline: { managedByPipe: boolean; gameDir?: string };
}

const DEFAULT_EMOJI = '🎮';
const DEFAULT_DESCRIPTION = 'Fresh from the pipe.';

export const ADMIN_QUERY_PARAM = 'admin';
export const ADMIN_QUERY_VALUE = 'true';

/**
 * "in-development" only shows with ?admin=true (exact, lowercase); "inactive"
 * never shows here; "active" always shows.
 */
export function isGameVisible(game: ManifestGame, searchParams: URLSearchParams): boolean {
  if (game.status === 'active') return true;
  if (game.status === 'in-development') {
    return searchParams.get(ADMIN_QUERY_PARAM) === ADMIN_QUERY_VALUE;
  }
  return false;
}

export function getVisibleGames(searchParams: URLSearchParams): ManifestGame[] {
  return (manifestData.games as ManifestGame[]).filter((game) => isGameVisible(game, searchParams));
}

// static-bundle games live outside the SPA router (public/games/<slug>/), so their
// actions need a real page load (<a href>) rather than a router <Link> or they 404.
export function getGameActions(game: ManifestGame): GameAction[] {
  const isExternal = game.type === 'static-bundle';
  if (game.actions && game.actions.length > 0) {
    return game.actions.map((action) => ({ ...action, external: action.external ?? isExternal }));
  }
  return [{ label: 'Play', to: game.entry, external: isExternal }];
}

export function getGameEmoji(game: ManifestGame): string {
  return game.emoji ?? DEFAULT_EMOJI;
}

export function getGameDescription(game: ManifestGame): string {
  return game.description ?? DEFAULT_DESCRIPTION;
}
